/**
 * Admin App — mounted at /admin/*
 * Keeps the existing JWT login flow and dashboard. Non-logged-in visitors
 * land on the login screen; authenticated staff see the clinic software.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../App.css';
import {
  Login,
  Dashboard,
  PatientRegistration,
  AppointmentsPage,
  MedicalForm,
  PrescriptionView,
  InvoiceGenerator,
  ServiceManagement,
  ReportsPage,
  UserManagement,
  PatientHistory,
  SlotScheduler,
  Navbar
} from './components';
import MarketingDashboard from './MarketingDashboard';

// ── Error Boundary ───────────────────────────────────────────────────────────
// Catches any runtime crash inside the admin SPA and shows a readable message
// instead of a completely blank / black screen.
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[AdminApp] Uncaught error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', background: '#f5f5f5', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
          fontFamily: 'sans-serif', color: '#1e293b', padding: 32
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '40px 48px', boxShadow: '0 4px 24px rgba(0,0,0,.12)', maxWidth: 500, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ marginBottom: 12, color: '#dc2626' }}>Something went wrong</h2>
            <p style={{ color: '#64748b', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
              The admin panel encountered an unexpected error. Please refresh the page. If the problem persists, contact support.
            </p>
            <code style={{ display: 'block', background: '#f1f5f9', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#475569', wordBreak: 'break-all', marginBottom: 24 }}>
              {this.state.error && this.state.error.toString()}
            </code>
            <button
              onClick={() => window.location.reload()}
              style={{ background: '#2C5F2D', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 15, cursor: 'pointer', fontWeight: 600 }}
            >
              🔄 Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Admin shell ──────────────────────────────────────────────────────────────
function AdminShell() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Derive sub-page from URL (e.g. /admin/patients → 'register-patient')
  useEffect(() => {
    const sub = location.pathname.replace(/^\/admin\/?/, '').split('/')[0] || 'dashboard';
    const pageMap = {
      '': 'dashboard',
      dashboard: 'dashboard',
      appointments: 'appointments',
      patients: 'register-patient',
      'medical-records': 'medical-form',
      prescriptions: 'prescriptions',
      invoices: 'invoices',
      services: 'services',
      reports: 'reports',
      users: 'users',
      history: 'patient-history',
      slots: 'slots',
      marketing: 'marketing',
      login: 'login'
    };
    setCurrentPage(pageMap[sub] || 'dashboard');
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userObj) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userObj));
    setIsAuthenticated(true);
    setUser(userObj);
    navigate('/admin/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/admin');
  };

  // Sidebar/Navbar page changer — translate to URL
  const goToPage = (page) => {
    const urlMap = {
      dashboard: '/admin/dashboard',
      appointments: '/admin/appointments',
      'register-patient': '/admin/patients',
      'medical-form': '/admin/medical-records',
      prescriptions: '/admin/prescriptions',
      invoices: '/admin/invoices',
      services: '/admin/services',
      reports: '/admin/reports',
      users: '/admin/users',
      'patient-history': '/admin/history',
      slots: '/admin/slots',
      marketing: '/admin/marketing',
    };
    navigate(urlMap[page] || '/admin/dashboard');
  };

  // Always render on a light background — the body has a dark background
  // (#060D05) set in index.html for the public site. Use an explicit inline
  // wrapper so the admin panel is always readable, regardless of which CSS
  // file loads first.
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: '#f5f5f5', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 18, color: '#2C5F2D', fontFamily: 'sans-serif'
      }}>
        🌿 Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    // Wrap Login in a light container so the dark body doesn't bleed through
    // before the login-container gradient kicks in.
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar user={user} onLogout={handleLogout} setCurrentPage={goToPage} />
      <div className="app-content">
        {currentPage === 'dashboard'    && <Dashboard user={user} setCurrentPage={goToPage} />}
        {currentPage === 'appointments' && <AppointmentsPage user={user} setCurrentPage={goToPage} />}
        {currentPage === 'register-patient' && (user.role === 'doctor' || user.role === 'receptionist' || user.role === 'admin') && <PatientRegistration user={user} />}
        {currentPage === 'medical-form' && (user.role === 'doctor' || user.role === 'admin') && <MedicalForm user={user} />}
        {currentPage === 'prescriptions' && <PrescriptionView user={user} />}
        {currentPage === 'invoices' && (user.role === 'doctor' || user.role === 'receptionist' || user.role === 'admin') && <InvoiceGenerator user={user} />}
        {currentPage === 'services' && (user.role === 'admin' || user.role === 'doctor') && <ServiceManagement user={user} />}
        {currentPage === 'reports' && user.role === 'admin' && <ReportsPage user={user} />}
        {currentPage === 'users' && user.role === 'admin' && <UserManagement user={user} />}
        {currentPage === 'patient-history' && (user.role === 'doctor' || user.role === 'admin') && <PatientHistory user={user} />}
        {currentPage === 'slots' && <SlotScheduler user={user} />}
        {currentPage === 'marketing' && (user.role === 'admin' || user.role === 'doctor') && <MarketingDashboard user={user} />}
      </div>
    </div>
  );
}

// Wrap the shell in the error boundary so any render crash shows a message
export default function AdminApp() {
  return (
    <AdminErrorBoundary>
      <AdminShell />
    </AdminErrorBoundary>
  );
}
